// Kiosk chrome — full-screen tablet frame with the novem top bar
// (wordmark · location · live clock). Port of the mockup KioskShell
// (pdks/kiosk.jsx): inline hex/var styles rewritten as Tailwind
// utilities over spec-44 semantic tokens; `tone="brand"` flips the
// whole frame to the accent surface for the success screen.

import { useTranslation } from "react-i18next";

import { useNow } from "@/lib/useNow";

export type KioskTone = "surface" | "brand";

export interface KioskShellProps {
  readonly tone?: KioskTone;
  readonly locationLabel?: string | null;
  readonly children: React.ReactNode;
}

export function KioskShell({
  tone = "surface",
  locationLabel = null,
  children,
}: KioskShellProps): React.ReactElement {
  const { t, i18n } = useTranslation();
  const now = useNow();
  const brand = tone === "brand";

  const clock = new Intl.DateTimeFormat(i18n.language, {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  }).format(now);

  return (
    <div
      className={`relative flex h-dvh w-full flex-col overflow-hidden ${
        brand ? "bg-[var(--accent)]" : "bg-[var(--bg-surface)]"
      }`}
    >
      {/* Top bar. flex-wrap + min-h (not fixed h): at narrow widths the
          location/clock group drops to its own row instead of colliding with
          the wordmark — justify-between has nothing left to give at 375px. */}
      <header className="flex min-h-[76px] shrink-0 flex-wrap items-center justify-between gap-x-6 gap-y-1 px-6 py-2 sm:px-11">
        <div className="flex shrink-0 items-center gap-3">
          <div
            aria-hidden="true"
            className={`flex h-[34px] w-[34px] items-center justify-center rounded-[9px] font-sans text-[16px] font-bold ${
              brand
                ? "bg-[var(--fg-on-accent)]/20 text-[var(--fg-on-accent)]"
                : "bg-[var(--accent)] text-[var(--fg-on-accent)]"
            }`}
          >
            {t("app.wordmark_base").slice(0, 1)}
          </div>
          <span
            className={`font-sans text-[24px] font-semibold tracking-[-0.02em] ${
              brand ? "text-[var(--fg-on-accent)]" : "text-[var(--fg-primary)]"
            }`}
          >
            {t("app.wordmark_base")}{" "}
            <span
              className={
                brand ? "text-[var(--fg-on-accent)]" : "text-[var(--accent)]"
              }
            >
              {t("app.wordmark_accent")}
            </span>
          </span>
        </div>
        <div className="flex min-w-0 items-center gap-[18px]">
          {/* min-w-0 + truncate: a longer-than-viewport location label (the
              bootstrap gates' default) ellipsizes instead of pushing the
              clock past the shell's overflow clip. */}
          <span
            className={`min-w-0 truncate font-serif text-[17px] italic ${
              brand ? "text-[var(--fg-on-accent)]" : "text-[var(--fg-muted)]"
            }`}
          >
            {locationLabel ?? t("kiosk.location_default")}
          </span>
          <span
            aria-hidden="true"
            className={`h-[22px] w-px shrink-0 ${
              brand ? "bg-[var(--fg-on-accent)]/25" : "bg-[var(--border-default)]"
            }`}
          />
          <time
            className={`shrink-0 font-mono text-[18px] font-medium tracking-[0.02em] tabular-nums ${
              brand ? "text-[var(--fg-on-accent)]" : "text-[var(--fg-primary)]"
            }`}
          >
            {clock}
          </time>
        </div>
      </header>

      <main className="flex min-h-0 flex-1 flex-col">
        {/* Every state inherits a document h1 (axe page-has-heading-one) from
            here — screen-level headings are visual, some states render none.
            Inside <main> because content outside a landmark trips axe's
            region rule even when visually hidden. */}
        <h1 className="sr-only">{t("app.title")}</h1>
        {children}
      </main>
    </div>
  );
}
